import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { DatabaseMetaInfo } from '../../../../types/electron';

/**
 * Result of inspecting a column for "enum-ness" — i.e. is it a finite,
 * knowable set of values that should drive a dropdown filter instead of a
 * free-text input?
 *
 * Declared takes priority (mirrors `categorizeJsonColumn`). Any column with
 * a `metaInfo.enumColumns` row is `'declared-enum'` regardless of underlying
 * type. Use `isNativeEnumColumn` when you need the engine-type distinction
 * (e.g. the picker's "Refresh values from data" button is disabled on native
 * MySQL ENUMs because the values are constrained by DDL).
 *
 * - `declared-enum` — user-declared via the MetaInfo manager / header glyph
 *   confirmation; lives in `metaInfo.enumColumns`. May or may not be a
 *   native enum column.
 * - `native-enum` — the column's engine type is a native enum (MySQL `ENUM`,
 *   Postgres user-defined enum type) and has no declaration row yet.
 * - `enum-eligible` — a short string-like type, or an int-like type whose
 *   name hints at a categorical role (status, kind, type, role, …). Drives
 *   the "Declare as enum?" affordance.
 * - `not-enum` — none of the above.
 */
export type EnumColumnCategory =
  | 'native-enum'
  | 'declared-enum'
  | 'enum-eligible'
  | 'not-enum';

/**
 * Where the `enum` operator should appear in the filter operator dropdown
 * for a given column. Mirrors `JsonOperatorPlacement`.
 *
 * - `default` — auto-selected, top of the list (native + declared enum
 *   columns: the dropdown picker is the expected primary filter).
 * - `last` — bottom of the list, not auto-selected (eligible columns where
 *   the user *could* filter by a sampled value set but `=`/`CONTAINS`
 *   free-text stays the default).
 * - `absent` — operator not offered.
 *
 * There is no `first` placement (unlike JSON): enum eligibility already
 * encodes the "strong hint" via the name-hint / short-type heuristic in
 * `categorizeEnumColumn`, so eligible always means "offer at the bottom".
 */
export type EnumOperatorPlacement = 'default' | 'last' | 'absent';

/**
 * Max length for a varchar/nvarchar column to be treated as eligible without
 * a name hint. Columns longer than this are usually free-form text (notes,
 * descriptions, JSON blobs) and shouldn't get an enum affordance by default.
 */
export const ENUM_ELIGIBLE_VARCHAR_THRESHOLD = 128;

/**
 * Lower-cased word tokens that, when present as a discrete word-boundary
 * token in a column name, suggest the column is categorical. Used for both
 * eligibility (int-typed columns) and as a tiebreaker for borderline string
 * types.
 */
export const ENUM_NAME_HINTS: readonly string[] = [
  'status',
  'state',
  'kind',
  'type',
  'subtype',
  'role',
  'level',
  'tier',
  'stage',
  'phase',
  'mode',
  'severity',
  'priority',
  'category',
];

export type EnumColumnInput = {
  engine: DatabaseEngineId;
  schema: string;
  table: string;
  name: string;
  /** Engine-specific column data type string, e.g. `enum('a','b')`, `varchar(32)`, `int`. */
  dataType: string;
  /**
   * Renderer-provided override: true when the engine reports this column's
   * type as a native enum but the `dataType` string can't reveal it.
   * Postgres' columns query resolves `dataType` via `format_type`, which
   * returns the enum's *own* type name (e.g. `mpaa_rating`), not
   * `USER-DEFINED`; that query also emits `pg_type.typtype = 'e'`, surfaced
   * here. Absent/false ⇒ fall back to type-string detection (unchanged
   * behavior for every other engine and for PG non-enum columns).
   */
  isNativeEnumType?: boolean;
};

type ParsedType = {
  /** Lowercased base type with no parens, e.g. `varchar`, `int`, `enum`. */
  base: string;
  /** Numeric length inside `(N)` if specified and finite, otherwise null. */
  length: number | null;
  /** True for MSSQL `(max)` length. */
  isMax: boolean;
  /** Raw lowercased input (preserves the enum value list etc.). */
  raw: string;
};

function parseDataType(raw: string): ParsedType {
  // `raw` is typed `string`, but callers feed it `column.dataType` which is
  // `string | undefined` at runtime (engine metadata gaps, session-restored
  // tabs whose columns predate a dataType field, etc.). A missing type must
  // degrade to the same neutral result an unparseable type yields — never
  // throw, since this runs per-column on every grid render and an
  // uncaught throw trips the app-wide ErrorBoundary.
  const lowered = (typeof raw === 'string' ? raw : '')
    .trim()
    .toLowerCase()
    .replace(/^character\s+varying\b/u, 'varchar')
    .replace(/^character(?!\s+varying)\b/u, 'char');
  // Enum/Set declarations like `enum('a','b','c')` have parens but the inner
  // text is the value list, not a length — special-case the base extraction.
  if (/^(?:enum|set)\b/.test(lowered)) {
    const base = lowered.startsWith('set') ? 'set' : 'enum';
    return { base, length: null, isMax: false, raw: lowered };
  }
  const match = lowered.match(/^([a-z0-9_ ]+?)\s*(?:\(\s*([^)]*?)\s*\))?(?:\s.*)?$/u);
  if (!match) return { base: lowered, length: null, isMax: false, raw: lowered };
  let base = (match[1] ?? '').trim().replace(/\s+/gu, ' ');
  if (base === 'bpchar') base = 'char';
  const lengthRaw = (match[2] ?? '').trim();
  const isMax = lengthRaw === 'max';
  const length = !isMax && /^\d+$/u.test(lengthRaw) ? parseInt(lengthRaw, 10) : null;
  return { base, length, isMax, raw: lowered };
}

/**
 * True when the column has a native enum type in its engine.
 *
 * - MySQL: any `ENUM(...)` or `SET(...)` declaration.
 * - Postgres: `USER-DEFINED` type (information_schema.columns.data_type).
 *   Real-engine flows confirm this against `pg_type.typtype = 'e'`; for
 *   renderer-side categorization we accept the USER-DEFINED hint and let the
 *   native extractor decide if it's truly an enum at fetch time. Falsely
 *   positive on, say, a composite type just means the user sees a "Configure
 *   enum column" glyph that returns no values on click — a tiny cost.
 * - Other engines: no native enum type.
 */
export function isNativeEnumColumn(column: EnumColumnInput): boolean {
  if (column.isNativeEnumType === true) return true;
  return isNativeEnum(column.engine, parseDataType(column.dataType));
}

function isNativeEnum(engine: DatabaseEngineId, parsed: ParsedType): boolean {
  switch (engine) {
    case 'mysql':
      return parsed.base === 'enum' || parsed.base === 'set';
    case 'pgsql':
      // information_schema reports user-defined enums as USER-DEFINED (raw
      // form is hyphen-cased; lowering keeps the comparison stable). NOTE:
      // the real-DB Table Explorer flow never reaches this string check —
      // pgsqlQueries.columns resolves dataType via pg_catalog.format_type
      // (the enum's own type name), so PG native enums are detected via the
      // EnumColumnInput.isNativeEnumType signal instead. This USER-DEFINED
      // path remains as a fallback for mock/test metadata.
      return parsed.base === 'user-defined' || parsed.raw.startsWith('user-defined');
    case 'mssql':
    case 'oracle':
    case 'sqlite':
    case 'kusto':
    case 'azurestorage':
      return false;
    case 'mock':
      return parsed.base === 'enum';
  }
}

function isShortStringType(engine: DatabaseEngineId, parsed: ParsedType): boolean {
  if (engine === 'azurestorage' && parsed.base === 'edm.string') return true;
  // Bounded string types where len ≤ threshold qualify as eligible. Native
  // enum / set columns are short by construction but those are handled
  // separately as `native-enum`.
  if (parsed.base === 'char' || parsed.base === 'nchar' || parsed.base === 'bpchar') {
    return parsed.length !== null && parsed.length <= ENUM_ELIGIBLE_VARCHAR_THRESHOLD;
  }
  if (parsed.base === 'varchar' || parsed.base === 'nvarchar' || parsed.base === 'varchar2' || parsed.base === 'nvarchar2') {
    if (parsed.isMax) return false;
    if (parsed.length === null) {
      // PG `varchar` with no length is unbounded — not eligible without a hint.
      return engine === 'pgsql' ? false : false;
    }
    return parsed.length <= ENUM_ELIGIBLE_VARCHAR_THRESHOLD;
  }
  return false;
}

function isIntLikeType(engine: DatabaseEngineId, parsed: ParsedType): boolean {
  // Cover the common int variants across engines. Float-likes are *not*
  // eligible — categorical numeric data is virtually always int-typed.
  // `long` is Kusto's 64-bit integer CSL type — but it is ENGINE-GATED because
  // Oracle's legacy `LONG` type is a large-text column (not an integer);
  // treating it as int-like would make an Oracle `LONG` column enum-eligible
  // and then blow up sampling with ORA-00997 ("illegal use of LONG datatype").
  if (parsed.base === 'long') return engine === 'kusto';
  return (
    parsed.base === 'int' ||
    parsed.base === 'integer' ||
    parsed.base === 'smallint' ||
    parsed.base === 'tinyint' ||
    parsed.base === 'bigint' ||
    parsed.base === 'int2' ||
    parsed.base === 'int4' ||
    parsed.base === 'int8' ||
    parsed.base === 'number'
  );
}

/**
 * Kusto `string` columns are UNBOUNDED — `getschema` reports a bare `string`
 * with no length, so `isShortStringType` (which requires a length ≤ threshold)
 * never matches them. Treat a Kusto `string` as enum-declarable in its own
 * right; the passive-eligibility name-hint gate is applied by the caller,
 * mirroring PG's unbounded-`varchar` treatment (eligible only with a hint).
 */
function isKustoEnumStringType(engine: DatabaseEngineId, parsed: ParsedType): boolean {
  return engine === 'kusto' && parsed.base === 'string';
}

/**
 * Split a column name into lower-cased word tokens on word boundaries:
 * non-alphanumerics (`_`, `-`, `.`, space) and camelCase / PascalCase /
 * ACRONYMWord case-changes. A digit followed by an uppercase letter is a
 * camelCase boundary (`tier1Status` → `["tier1", "status"]`); a digit at
 * word-end stays with its token (`tier1` → `["tier1"]`).
 */
function tokenizeColumnName(name: string): string[] {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

export function nameSuggestsEnum(name: string): boolean {
  if (!name) return false;
  const tokens = tokenizeColumnName(name);
  return tokens.some((t) => (ENUM_NAME_HINTS as readonly string[]).includes(t));
}

/**
 * True when a column's *type* is one where declaring an enum is sensible —
 * a short string type or any int-like type — regardless of its name. Native
 * enum types return `false` (they're surfaced via `isNativeEnumColumn` and
 * get the "Configure" path, not "Declare").
 *
 * This is intentionally broader than `categorizeEnumColumn`'s
 * `enum-eligible` (which also name-gates int columns and is what drives the
 * passive `enum` operator + the proactive `▾?` glyph). The explicit,
 * right-click "Declare as enum" affordance should be available on *any*
 * column where it makes sense — short text or integer — even one whose name
 * gives no hint. Float/decimal/date/blob/long-text types stay excluded.
 */
export function isEnumDeclarableType(column: EnumColumnInput): boolean {
  if (column.isNativeEnumType === true) return false;
  if (!supportsEnumExtraction(column.engine)) return false;
  const parsed = parseDataType(column.dataType);
  if (isNativeEnum(column.engine, parsed)) return false;
  return (
    isShortStringType(column.engine, parsed) ||
    isIntLikeType(column.engine, parsed) ||
    isKustoEnumStringType(column.engine, parsed)
  );
}

/**
 * Build the set of `${schema}.${table}.${column}` keys the user has dismissed
 * the enum ▾? glyph for. The key format MUST stay byte-identical to
 * `dismissalKey` in electron/metaInfo/metaInfoStore.ts (the main process
 * persists/dedupes with the same format) — do not change one without the other.
 */
export function buildDismissedEnumKeySet(
  metaInfo: DatabaseMetaInfo | null | undefined,
): Set<string> {
  const ids = new Set<string>();
  for (const d of metaInfo?.enumGlyphDismissals ?? []) {
    ids.add(`${d.schema}.${d.table}.${d.column}`);
  }
  return ids;
}

/**
 * Engines with NO enum-value extractor registered in
 * `electron/metaInfo/enumValueExtraction.ts` (see
 * `electron/metaInfo/engines/index.ts`). The renderer can't see that registry
 * directly, so this list is a manually-synced mirror of its gap: register a
 * working extractor for an engine there, then remove it from this list.
 *
 * Columns on an unsupported engine must never be surfaced as
 * `enum-eligible` or `isEnumDeclarableType`-true — every entry point
 * (glyph, filter chip, "Declare as enum" menu item) leads to
 * `fetchEnumValuesFor` throwing "No enum extractor registered for engine
 * '<engine>'". Already-`declared-enum` columns are unaffected: a prior
 * declaration is valid MetaInfo and must keep rendering, not crash.
 *
 * All five SQL engines + kusto (sampled-only) + mock now have extractors, so
 * this list is empty; it stays as the seam for any future engine.
 */
const ENUM_EXTRACTION_UNSUPPORTED_ENGINES: readonly DatabaseEngineId[] = [];

function supportsEnumExtraction(engine: DatabaseEngineId): boolean {
  return !ENUM_EXTRACTION_UNSUPPORTED_ENGINES.includes(engine);
}

function isDeclared(
  column: EnumColumnInput,
  metaInfo: DatabaseMetaInfo | null | undefined,
): boolean {
  if (!metaInfo) return false;
  return metaInfo.enumColumns.some(
    (decl) =>
      decl.schema === column.schema &&
      decl.table === column.table &&
      decl.column === column.name,
  );
}

/**
 * Categorize a column by enum-ness. Declared declarations trump everything
 * else; native enum types are recognized for MySQL and Postgres; otherwise
 * a heuristic over the column type and name decides eligibility.
 */
export function categorizeEnumColumn(
  column: EnumColumnInput,
  metaInfo: DatabaseMetaInfo | null | undefined,
): EnumColumnCategory {
  if (isDeclared(column, metaInfo)) return 'declared-enum';
  const parsed = parseDataType(column.dataType);
  if (column.isNativeEnumType === true || isNativeEnum(column.engine, parsed)) return 'native-enum';
  // No registered enum-value extractor for this engine — never propose a
  // NEW eligible column (declared columns are handled above already).
  if (!supportsEnumExtraction(column.engine)) return 'not-enum';
  // Short string columns are eligible regardless of name (a `varchar(32)` is
  // the canonical status-column shape).
  if (isShortStringType(column.engine, parsed)) return 'enum-eligible';
  // Int-like columns need a name hint to avoid flagging every primary key /
  // counter as enum-eligible.
  if (isIntLikeType(column.engine, parsed) && nameSuggestsEnum(column.name)) return 'enum-eligible';
  // Kusto `string` is unbounded — name-hint gated like an int-like column, so a
  // `Message`/`Description` column doesn't sprout a chip while `EventType`/
  // `State`/`Level` does.
  if (isKustoEnumStringType(column.engine, parsed) && nameSuggestsEnum(column.name)) {
    return 'enum-eligible';
  }
  return 'not-enum';
}

/**
 * The FK arm of the proactive `▾?` hint: a column that resolves to exactly one
 * unconditional FK target (real or loose — `resolveLookupEnumTarget`) AND whose
 * name reads categorical earns the hint even when its TYPE alone never would
 * — a `varchar(255) severity_code`, a `bigint status_id` on an engine where
 * that isn't hint-eligible. FK-ness plus a categorical name is a stronger
 * signal than either half, so the hint stays rare and high-precision.
 *
 * This is a UNION with the existing type-based rule, not a replacement: the
 * caller ORs the two and applies ONE `enumGlyphDismissals` intersection over
 * the result, so a dismissal silences both arms identically.
 *
 * `hasLookupTarget` is passed in rather than resolved here — each grid feeds
 * `resolveLookupEnumTarget` from its own FK source (Table Explorer reads
 * `column.fkInfo`, the results grid the result-set FK map), so only the
 * decision is shared, never the plumbing.
 *
 * Declared and native columns are excluded: they already carry their own,
 * louder glyph.
 */
export function fkColumnSuggestsEnum(
  column: EnumColumnInput,
  metaInfo: DatabaseMetaInfo | null | undefined,
  hasLookupTarget: boolean,
): boolean {
  if (!hasLookupTarget) return false;
  if (!supportsEnumExtraction(column.engine)) return false;
  if (!nameSuggestsEnum(column.name)) return false;
  const category = categorizeEnumColumn(column, metaInfo);
  return category !== 'declared-enum' && category !== 'native-enum';
}

/**
 * Resolve where the `enum` operator should be placed in the filter operator
 * dropdown for a column. See `EnumOperatorPlacement`. Mirrors
 * `inferJsonOperatorPlacement`.
 */
export function inferEnumOperatorPlacement(
  column: EnumColumnInput,
  metaInfo: DatabaseMetaInfo | null | undefined,
): EnumOperatorPlacement {
  const category = categorizeEnumColumn(column, metaInfo);
  switch (category) {
    case 'native-enum':
    case 'declared-enum':
      return 'default';
    case 'enum-eligible':
      return 'last';
    case 'not-enum':
      return 'absent';
  }
}
